import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  ReactNode,
} from 'react';

import SignUp from './SignUp';

type Action = {
  type:
    | 'SET_EMAIL'
    | 'SET_PASSWORD'
    | 'SET_REFERRAL_CODE'
    | 'SET_LOADING'
    | 'SET_STEP'
    | 'CLEAR';
  payload: any;
};
type Dispatch = (action: Action) => void;
type State = {
  email: string;
  password: string;
  referralCode: string;
  step: 'INITIAL_FORM' | 'FINAL_FORM' | 'CONFIRM_EMAIL';
  isLoading: boolean;
};

const initialState = {
  email: '',
  password: '',
  referralCode: '',
  step: 'INITIAL_FORM',
  isLoading: false,
};

const Context = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined
);

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_EMAIL': {
      return { ...state, email: action.payload };
    }
    case 'SET_PASSWORD': {
      return { ...state, password: action.payload };
    }
    case 'SET_REFERRAL_CODE': {
      return { ...state, referralCode: action.payload };
    }
    case 'SET_STEP': {
      return { ...state, step: action.payload };
    }
    case 'SET_LOADING': {
      return { ...state, isLoading: action.payload };
    }
    case 'CLEAR': {
      return { ...initialState, ...action.payload };
    }
    default: {
      return state;
    }
  }
};

export const SignUpProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState });

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useSignUpContext = () => {
  const context = useContext(Context);
  if (context === undefined) {
    throw new Error('useSignUpContext must be used within a SignUpProvider');
  }

  return context;
};

export const SignUpPage: React.FC = () => (
  <SignUpProvider>
    <SignUp />
  </SignUpProvider>
);
